import type { CepLookupResult, EnderecoDoCep } from '@/shared/types/cep';

import { getApiErrorMessage, isRecord } from './apiResponse';
import { apiGet } from './requestBuilder';

const CEP_NAO_ENCONTRADO = 'CEP não encontrado';
const CEP_INDISPONIVEL = 'Não foi possível consultar o CEP agora.';
const MUITAS_CONSULTAS = 'Muitas consultas de CEP agora. Aguarde um momento e tente de novo.';

/**
 * Consulta o CEP na rota pública do core para completar um formulário de endereço.
 *
 * Pública e com `skipAuth` de propósito: o onboarding da empresa consulta o CEP antes de
 * existir sessão. Sem `skipAuth`, qualquer 401 aqui dispararia a renovação de sessão, que
 * falha sem sessão e marca a sessão local como inválida.
 *
 * A tela nunca pode acusar o endereço quando o problema é nosso: o 503 do core já traz a
 * mensagem de indisponível no envelope, e erro sem envelope (502/504 do balanceador) cai na
 * mesma mensagem pelo status. O 429 tem texto próprio porque a cota por IP responde com o
 * corpo padrão do `@fastify/rate-limit`, em inglês.
 *
 * @example const resultado = await lookupCep('13010-210'); if (resultado.valid) resultado.endereco;
 */
export async function lookupCep(cep: string): Promise<CepLookupResult> {
  const cepLimpo = cep.replace(/\D/g, '');
  if (cepLimpo.length !== 8) return { valid: false, error: 'CEP deve conter 8 dígitos' };

  const response = await apiGet(`/integracoes/cep/${cepLimpo}`, undefined, true);
  const body: unknown = await response.json().catch(() => null);

  if (response.status === 429) return { valid: false, error: MUITAS_CONSULTAS };
  if (!response.ok) {
    const padrao = response.status >= 500 ? CEP_INDISPONIVEL : CEP_NAO_ENCONTRADO;
    return { valid: false, error: getApiErrorMessage(body, padrao) };
  }

  const endereco = isRecord(body) ? body.data : undefined;
  if (!isEnderecoDoCep(endereco)) return { valid: false, error: CEP_NAO_ENCONTRADO };

  return { valid: true, endereco };
}

function isEnderecoDoCep(valor: unknown): valor is EnderecoDoCep {
  if (!isRecord(valor)) return false;
  return ['cep', 'logradouro', 'bairro', 'cidade', 'uf'].every(
    (campo) => typeof valor[campo] === 'string',
  );
}
